import React from 'react';
import PropTypes from 'prop-types';

import Translate from '@hydra/common/components/i18n';
import RatingIcons from '@hydra/common/components/RatingIcons';
import SafeAnchor from '@hydra/common/components/SafeAnchor';
import ReviewSkeleton from '@hydra/common/components/ReviewSkeleton';

import CriteriumTable from '@hydra/common/criterium/CriteriumTable';
import Criterium from '@hydra/common/criterium/Criterium';
import { GoogleAnalyticsDataAuthWrapper } from '@hydra/common/components/GoogleAnalytics/GoogleAnalyticsDataWrapper';
import { useReviewContext } from '@hydra/common/context/review';
import { usePdfTemplateContext } from '@hydra/common/context/pdfTemplate';

const getDomainRows = (domain, isPdf, pdfTheme) => domain.map(item => {
  const isModern = isPdf && pdfTheme === 'modern';
  const stars = Math.min(Math.round(item.pageviewsPerVisit), 3);
  return [
    <SafeAnchor href={`http://${item.source}`} rel='nofollow noopener noreferrer' target='_blank' key={item.source}>{item.source}</SafeAnchor>,
    <span key='visits'>{item.sessions}</span>,
    <RatingIcons type='rating' size={isModern ? 'md' : 'xs'} rating={stars} key='rating' />
  ];
});

function rowsToTableData (rows) {
  return (rows || [])
    .slice(0, 20)
    .map(([source, sessions, bounceRate, avgSessionDuration, pageviewsPerSession]) => ({
      source,
      sessions,
      bounceRate,
      stars: Math.min(3, pageviewsPerSession),
      avgSessionDuration,
      pageviewsPerSession
    }));
}

const report = {
  reportType: 'ga',
  query: {
    metrics: 'ga:sessions,ga:bounceRate,ga:avgSessionDuration,ga:pageviewsPerSession',
    dimensions: 'ga:source',
    sort: '-ga:sessions',
    segment: 'dynamic::ga:medium==referral',
    'end-date': 'today',
    'start-date': '7daysAgo'
  }
};

function TrafficBacklinksTable ({ data }) {
  const { isPdf } = useReviewContext();
  const { pdfTheme } = usePdfTemplateContext();
  const chartData = rowsToTableData(data);
  const domainHead = [
    <Translate content='pj_ga_source' key='pj_ga_source' />,
    <Translate content='pj_ga_visits' key='pj_ga_visits' />,
    <Translate content='pj_backlinks_quality' key='pj_backlinks_quality' />
  ];

  return (
    <CriteriumTable
      fixedColumns={[60, 20, 20]}
      head={domainHead}
      rows={getDomainRows(chartData, isPdf, pdfTheme)}
      thinHead
      striped
      nowrap
    />
  );
}

export const PjTrafficBacklinks = ({ criterium, url }) => {
  return (
    <Criterium
      criterium={criterium || { type: 'pj_traffic_backlinks', score: null, importance: 0, solvability: 0 }}
      variables={{ url }}
      clickable={!!criterium}
    >
      {criterium
        ? (
          <GoogleAnalyticsDataAuthWrapper
            accessToken={criterium.data && criterium.data.accessToken}
            viewId={criterium.data && criterium.data.viewId}
            report={report}
            Component={TrafficBacklinksTable}
            loadingComponent={<ReviewSkeleton height={256} />}
          />
          )
        : <ReviewSkeleton height={256} />}
    </Criterium>
  );
};

PjTrafficBacklinks.propTypes = {
  criterium: PropTypes.shape({
    data: PropTypes.shape({
      viewId: PropTypes.string,
      accessToken: PropTypes.string
    })
  })
};

export default PjTrafficBacklinks;
